import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Container,
  Breadcrumbs,
  Link as MuiLink,
  Avatar,
  Stack,
  useTheme,
  IconButton,
  Tooltip,
  Fade,
  Skeleton,
  Badge
} from "@mui/material";
import {
  DirectionsCar,
  Person,
  Description,
  Assignment,
  Security,
  Add as AddIcon,
  Remove as RemoveIcon,
  NavigateNext,
  ChevronRight,
  Badge as BadgeIcon,
  Palette,
  CalendarToday,
  Visibility,
  Download,
  Edit,
  Share,
  Verified,
  Speed,
  LocalGasStation
} from "@mui/icons-material";
import Spinner from "../Spinner";
import { fetchDocument, fetchVehicleImages } from "../fetchDocs";
import FilePreview from "./FilePreview";
import AssignDriverForm from "./AssignDriverForm";
import DeleteConfirmation from "../deleteConfirmation";
import { LucideCalendarSearch } from "lucide-react";
import { PROVIDER_POSITIONS } from "../enum";

function ViewVehicle() {
  const theme = useTheme();
  const [vehicle, setVehicle] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [vehicleImage, setVehicleImage] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [driverToRemove, setDriverToRemove] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const { id } = useParams();

  const fetchData = async () => {
    try {
      const res = await axios.get(`/vehicle/${id}`);
      setVehicle(res.data.vehicle);
      setDrivers(res.data.drivers);
      fetchDocument(res.data.vehicle.documents, setDocuments);
      fetchVehicleImages(res.data.vehicle.vehicleImage, setVehicleImage);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load vehicle details");
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleRemoveDriverClick = (driverId) => {
    setDriverToRemove(driverId);
    setDeleteMessage("Are you sure you want to remove this driver?");
    setDisplayConfirmationModal(true);
  };

  const handleRemoveDriver = async (driverId) => {
    try {
      await axios.post(`/removeDriverVehicle/${vehicle.id}`, {
        vehicleId: vehicle.id,
        driverId: [driverId],
      });
      setDisplayConfirmationModal(false);
      toast.success("Driver removed successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove driver");
    }
  };

  if (loading) return <Spinner />;

  const documentTypes = [
    { key: 'documents', label: 'Legal Documents', icon: <Security />, color: '#2196F3' },
    { key: 'tradeLicence', label: 'Trade License', icon: <Assignment />, color: '#FF9800' },
    { key: 'insurance', label: 'Insurance', icon: <Verified />, color: '#4CAF50' }
  ];

  return (
    <Box 
      sx={{ 
        bgcolor: 'grey.50',
        minHeight: '100vh',
        py: { xs: 2, md: 4 },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}
    >
      <Container maxWidth="xl">
        {/* Enhanced Header Section */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 6 }}>
            <Breadcrumbs 
              separator={<NavigateNext fontSize="small" />}
              sx={{ 
                mb: 3,
                '& .MuiBreadcrumbs-separator': { color: 'primary.main' }
              }}
            >
              <MuiLink 
                component={Link} 
                to="/vehicleList" 
                sx={{ 
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.3s'
                }}
              >
                Vehicles
              </MuiLink>
              <Typography color="primary.main" fontWeight={600}>
                {vehicle.plateNumber}
              </Typography>
            </Breadcrumbs>
            
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={3}
              sx={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                borderRadius: 3,
                p: 4,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              <Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  {vehicle.model}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ opacity: 0.8 }}>
                  Vehicle Management System
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <Tooltip title="Edit Vehicle">
                  <IconButton 
                    sx={{ 
                      bgcolor: 'white',
                      boxShadow: theme.shadows[2],
                      '&:hover': { bgcolor: 'grey.100', transform: 'translateY(-2px)' },
                      transition: 'all 0.3s'
                    }}
                  >
                    <Edit color="primary" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Share">
                  <IconButton 
                    sx={{ 
                      bgcolor: 'white',
                      boxShadow: theme.shadows[2],
                      '&:hover': { bgcolor: 'grey.100', transform: 'translateY(-2px)' },
                      transition: 'all 0.3s'
                    }}
                  >
                    <Share color="primary" />
                  </IconButton>
                </Tooltip>

                {vehicle?.vehicleType?.provider_type === PROVIDER_POSITIONS.AMBULANCE_PROVIDER && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAssignModal(true)}
                    sx={{ 
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      '&:hover': { 
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    Assign Driver
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          {/* Enhanced Left Column - Vehicle Showcase */}
          <Grid item xs={12} lg={5}>
            <Fade in timeout={1000}>
              <Card 
                sx={{ 
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-5px)' }
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Hero Image Section */}
                  <Box sx={{ 
                    position: 'relative',
                    height: 300,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {vehicleImage ? (
                      <FilePreview
                        filePath={vehicleImage}
                        altText="Vehicle"
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <DirectionsCar sx={{ fontSize: 80, color: 'white', opacity: 0.7 }} />
                    )}
                    
                    {/* Floating Status Badge */}
                    <Chip
                      label="ACTIVE"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: '#4CAF50',
                        color: 'white',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
                      }}
                    />
                  </Box>

                  {/* Vehicle Info Cards */}
                  <Box sx={{ p: 4 }}>
                    {/* Primary Info */}
                    <Stack spacing={3} sx={{ mb: 4 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip
                          label={vehicle.plateNumber}
                          icon={<BadgeIcon />}
                          sx={{ 
                            fontSize: '1.1rem',
                            py: 3,
                            px: 4,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontWeight: 700,
                            letterSpacing: 1,
                            boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)'
                          }}
                        />
                      </Box>
                    </Stack>

                    {/* Quick Stats Grid */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            textAlign: 'center',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #FF6B6B, #FF8E53)',
                            color: 'white',
                            transition: 'transform 0.3s',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                        >
                          <DirectionsCar sx={{ fontSize: 28, mb: 1 }} />
                          <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                            Model
                          </Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {vehicle.model}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            textAlign: 'center',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #4ECDC4, #44A08D)',
                            color: 'white',
                            transition: 'transform 0.3s',
                            '&:hover': { transform: 'scale(1.05)' }
                          }}
                        >
                          <Palette sx={{ fontSize: 28, mb: 1 }} />
                          <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                            Color
                          </Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {vehicle.color}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            textAlign: 'center',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #A8EDEA, #FED6E3)',
                            color: 'text.primary',
                            transition: 'transform 0.3s',
                            '&:hover': { transform: 'scale(1.02)' }
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 28, mb: 1, color: 'primary.main' }} />
                          <Typography variant="caption" display="block" color="text.secondary">
                            Manufacturing Year
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="primary.main">
                            {vehicle.manufacturedYear}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* Enhanced Right Column - Details */}
          <Grid item xs={12} lg={7}>
            <Stack spacing={4}>
              {/* Documents Section */}
              <Fade in timeout={1200}>
                <Paper 
                  sx={{ 
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}
                    >
                      <Description />
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      Vehicle Documents
                    </Typography>
                  </Stack>
                  
                  <Grid container spacing={3}>
                    {documentTypes.map((docType, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Card 
                          sx={{ 
                            borderRadius: 3,
                            overflow: 'hidden',
                            transition: 'all 0.3s',
                            border: `2px solid ${docType.color}20`,
                            '&:hover': { 
                              transform: 'translateY(-8px)',
                              boxShadow: `0 15px 30px ${docType.color}30`,
                              borderColor: docType.color
                            }
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              background: `linear-gradient(135deg, ${docType.color}15, ${docType.color}05)`,
                              borderBottom: `2px solid ${docType.color}20`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ color: docType.color }}>
                                {docType.icon}
                              </Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {docType.label}
                              </Typography>
                            </Stack>
                          </Box>
                          
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                height: 120,
                                borderRadius: 2,
                                bgcolor: 'grey.50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2
                              }}
                            >
                              <FilePreview
                                filePath={vehicle[docType.key]}
                                altText={docType.label}
                                style={{ maxHeight: '100%', maxWidth: '100%' }}
                              />
                            </Box>
                            
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="View Document">
                                <IconButton size="small" sx={{ color: docType.color }}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download">
                                <IconButton size="small" sx={{ color: docType.color }}>
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Fade>

              {/* Enhanced Drivers Section */}
              <Fade in timeout={1400}>
                <Paper 
                  sx={{ 
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #FF6B6B, #FF8E53)',
                          color: 'white'
                        }}
                      >
                        <Person />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="text.primary">
                          Assigned Drivers
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {drivers.length} driver{drivers.length !== 1 ? 's' : ''} assigned
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Chip
                      label={drivers.length}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 700,
                        minWidth: 40,
                        height: 40
                      }}
                    />
                  </Stack>
                  
                  {drivers.length > 0 ? (
                    <List disablePadding sx={{ bgcolor: 'transparent' }}>
                      {drivers.map((driver, index) => (
                        <React.Fragment key={driver.id}>
                          <ListItem
                            sx={{
                              px: 3,
                              py: 2,
                              borderRadius: 3,
                              mb: 2,
                              background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              transition: 'all 0.3s',
                              '&:hover': { 
                                transform: 'translateX(10px)',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                bgcolor: 'white'
                              }
                            }}
                          >
                            <ListItemText
                              primary={
                                <MuiLink
                                  component={Link}
                                  to={`/viewDriver/${driver.id}`}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    color: 'inherit'
                                  }}
                                >
                                  <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          borderRadius: '50%',
                                          bgcolor: '#4CAF50',
                                          border: '2px solid white'
                                        }}
                                      />
                                    }
                                  >
                                    <Avatar 
                                      sx={{ 
                                        mr: 3,
                                        width: 50,
                                        height: 50,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        fontSize: '1.2rem',
                                        fontWeight: 700
                                      }}
                                    >
                                      {driver.firstName[0]}{driver.lastName[0]}
                                    </Avatar>
                                  </Badge>
                                  
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600} color="text.primary">
                                      {driver.firstName} {driver.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Professional Driver
                                    </Typography>
                                  </Box>
                                  
                                  <ChevronRight sx={{ color: 'primary.main', fontSize: 28 }} />
                                </MuiLink>
                              }
                            />
                            
                            {vehicle?.vehicleType?.provider_type === PROVIDER_POSITIONS.AMBULANCE_PROVIDER && drivers.length > 1 && (
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<RemoveIcon />}
                                onClick={() => handleRemoveDriverClick(driver.id)}
                                sx={{ 
                                  borderRadius: 2,
                                  ml: 2,
                                  '&:hover': { 
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.3s'
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center',
                      py: 6,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
                      border: '2px dashed rgba(0,0,0,0.1)'
                    }}>
                      <Person sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No drivers assigned
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        Add a driver to get started with vehicle operations
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Fade>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <AssignDriverForm
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        vehicleId={vehicle.id}
        onSuccess={fetchData}
      />

      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={handleRemoveDriver}
        hideModal={() => setDisplayConfirmationModal(false)}
        id={driverToRemove}
        message={deleteMessage}
      />
    </Box>
  );
}

export default ViewVehicle;